import * as zod from "zod";
import type {
    IPCComponenteVm,
    IPCInsumoVm,
    IPCMateriaPrimaVm,
    IPCProdutoAcabadoVm
} from "@/modules/pedidoCompra/type/itensPedidoCompra.type";
import type {ContaVm} from "@/modules/extrato";

export interface PedidoCompraVm {
    pedidoCompraId: number;
    statusAprovacao: number;
    statusAprovacaoDescricao?: string;
    fornecedorId: number;
    fornecedorNomeFantasia: string;
    formaPagamentoId?: number;
    formaPagamentoDescricao?: string;
    condicaoPagamento?: string;
    transportadoraId: number;
    transportadoraNome?: string;
    contatoId?: number;
    contatoNome?: string;
    tipoFrete: number;
    tipoFreteDescricao?: string;
    valorFrete?: number;
    valorIcms: number;
    valorIpi: number;
    localEntrega?: string;
    dataEmissao?: Date;
    dataEmissaoFormatada?: string;
    usuarioEmissao?: string;
    dataAjusteStatus?: Date;
    dataAjusteStatusAjustada?: string;
    usuarioAjusteStatus?: string;
    observacao?: string;
    valorTotalPedido?: number;
    ipcMateriaPrima?: IPCMateriaPrimaVm[];
    ipcInsumo?: IPCInsumoVm[];
    ipcComponente?: IPCComponenteVm[];
    ipcProdutoAcabado?: IPCProdutoAcabadoVm[];
    itens?: PedidoCompraItemVm[];
    pedidoCompraLancamento?: PedidoCompraLancamentoVm[];
    lancamentos?: PedidoCompraLancamentoVm[];
    itensCarregados?: PedidoCompraItemVm[];
    nuSequencia?: number;

}


export function dataAsPedidoCompraVm(data: any): PedidoCompraVm {
    return data as PedidoCompraVm;
}

export interface PedidoCompraLancamentoItemVm {
    pedidoCompraLancamentoId?: number;
    data: Date;
    valor: number;
    lancamento?: number;
    status?: number;
    statusDescricao?: string;
    especieCobrancaId?: number;
    especieCobrancaDescricao?: string;
    parcela?: string;
    situacaoFinanceira?: number;
    situacaoFinanceiraDescricao?: string;
    saldoAberto?: number;
    valorBaixadoAcumulado?: number;
    conta?: ContaVm | null;
    naturezaFinanceiraId?: number | null;
    naturezaFinanceiraDescricao?: string | null;
}

export interface PedidoCompraLancamentoVm {
    pedidoCompraLancamentoId: number;
    especieCobrancaId?: number;
    especieCobrancaDescricao?: string;
    data: Date;
    dataFormatada: string;
    valor: number;
    lancamento: number;
    status: number;
    statusDescricao: string;
    parcela: string;
    situacaoFinanceira: number;
    situacaoFinanceiraDescricao: string;
    saldoAberto: number;
    valorBaixadoAcumulado: number;
    conta?: ContaVm | null;
    naturezaFinanceiraId?: number | null;
    naturezaFinanceiraDescricao?: string | null;
}

export function dataAsPedidoCompraLancamentoVm(data: any): PedidoCompraLancamentoVm {
    return data as PedidoCompraLancamentoVm;
}

// Mesmo domínio do backend ESituacaoFinanceiraLancamento (Previsto=1/EmAberto=2/Parcial=3/Baixado=4/
// Cancelado=5), também usado no módulo lancamento (ver SITUACAO_FINANCEIRA_POR_CODIGO em
// lancamento.type.ts). Aqui fica com chave NUMÉRICA porque PedidoCompraLancamentoVm.situacaoFinanceira
// chega como int puro do backend — sem a conversão pra union de string que o módulo lancamento faz.
export enum ESituacaoFinanceiraLancamentoPedidoCompra {
    Previsto = 1,
    EmAberto = 2,
    Parcial = 3,
    Baixado = 4,
    Cancelado = 5,
}

// Mesmas severidades de situacaoFinanceiraInfo (lancamento.labels.ts) — usado no badge da lista de
// lançamentos do pedido, tanto no cadastro/alteração quanto na listagem.
export const SEVERIDADE_SITUACAO_FINANCEIRA_LANCAMENTO_PEDIDO: Record<number, string> = {
    1: "info",
    2: "info",
    3: "warn",
    4: "success",
    5: "danger",
};

export interface PedidoCompraItemVm {
    id: number;
    itemNovo?: boolean;
    itemId?: string;
    codigo?: string;
    artigoId?: number;
    classificacaoArtigoId?: number;
    classificacaoArtigoDescricao?: string;
    /** Só preenchido para item legado (itemNovo=false) — item novo vem sempre 0/null, ver classificacaoArtigoId/classificacaoArtigoDescricao. */
    tipoItem: number;
    tipoItemDescricao: string;
    unidade: string;
    produtoId: number;
    descricao: string;
    quantidade: number;
    valorUnitario: number;
    valorTotal: number;
    prazoEntrega?: Date;
    prazoEntregaFormatada?: string;
    statusEntrega: number;
    statusEntregaDescricao: string;
    dimensao1?: number;
    dimensao2?: number;
    dimensao3?: number;
    rtMin?: number;
    rtMax?: number;
    tolerancia?: number;
    embalagem?: string;
    condicoesEspeciais?: string;
    dataEmenda?: Date;
    dataEmendaFormatada?: string;
    nuSequencia?: number;
}

export function dataAsPedidoCompraItemVm(data: any): PedidoCompraItemVm {
    return data as PedidoCompraItemVm;
}

/** EStatusPedidoCompraItem — status do item novo. Não confundir com EStatusEntregaMateriaPrima (item antigo). */
export enum EStatusPedidoCompraItem {
    Aguardando = 1,
    RecebidoParcial = 2,
    Recebido = 3,
    Cancelado = 4
}


export interface PedidoCompraItemFormVm {
    id?: string | null;
    artigoId?: number | null;
    codigo?: string;
    descricao: string;
    classificacaoArtigoId?: number;
    classificacaoArtigoDescricao?: string;
    unidade?: string;
    observacao?: string;
    quantidade: number;
    valorUnitario: number;
    prazo: Date | null;
    statusEntrega?: number;
    statusEntregaDescricao?: string;
    nuSequencia?: number;
}

export function dataAsPedidoCompraItemFormVm(data: any): PedidoCompraItemFormVm {
    return data as PedidoCompraItemFormVm;
}


export enum EStatusPedidoCompra {
    Nada = 0,
    Aprovado = 1,
    Reprovado = 2
}

export enum ETipoFretePedidoCompra {
    Emitente = 1,
    Destinatario = 2,
    Redespacho = 3
}

//TODO Depois se for criar vm desse itens passar eles para seus correspondentes

export const zodTransportadoraVm = zod.object({
    transportadoraId: zod.number(),
    nomeTransportadora: zod.string(),
}, {message: "Selecione uma transportadora"}).refine((val) => val != null);

export const zodTransportadoraOpcionalVm = zod.object({
    transportadoraId: zod.number(),
    nomeTransportadora: zod.string(),
}, {message: "Selecione uma transportadora"}).nullable();